// Kyte bag helpers — read-only offer summary + ancillaries-catalog normalizer.
// Kyte's pre-book bag flow runs through the unified ShopAncillaries call
// (kyte.ancillaries), not a standalone bags slice.

import {
  minor_to_decimal,
  as_number,
  as_array,
  as_boolean,
  bag_quantity,
} from '@liveflight/providers/base/helpers';
import { currency_symbol } from '@resources';
import {
  get_kyte_ancillary_info,
  NOT_SOLD_ANCILLARY_CODES,
} from './resources/ancillaries';
import { collect_traveler_ids } from './helpers';

/**
 * Per-itinerary tally of cabin / included / chargeable bags for the first
 * segment plus a per-traveler breakdown. `extra` stays false on Kyte: extras
 * availability lives in the async ShopAncillaries catalog, not the offer.
 * @param {object} flight_offer
 * @param {number} [itinerary_index=0]
 * @returns {object}
 */
export function get_bags(flight_offer, itinerary_index = 0) {
  const parsed = {
    extra: false,
    travelers: [],
    cabin: 0,
    hand: 0,
    included: 0,
    chargeable: 0,
    total: 0,
  };

  if (!flight_offer) return parsed;
  const { travelerPricings = [], itineraries = [] } = flight_offer;
  if (itinerary_index < 0 || itinerary_index >= itineraries.length)
    {return parsed;}

  const { segments: itinerary_segments = [] } =
    itineraries[itinerary_index] || {};
  if (itinerary_segments.length === 0) return parsed;
  const first_segment_id = (itinerary_segments[0] || {}).id;
  if (!first_segment_id) return parsed;

  travelerPricings.forEach((traveler_pricing) => {
    if (!traveler_pricing) return;
    const fare_detail = (traveler_pricing.fareDetailsBySegment || []).find(
      (f) => f && f.segmentId === first_segment_id
    );
    const traveler = {
      traveler_id: traveler_pricing.travelerId,
      traveler_type:
        traveler_pricing.traveler_type || traveler_pricing.travelerType,
      cabin: 0,
      hand: 0,
      included: 0,
      chargeable: 0,
    };

    if (fare_detail) {
      traveler.included = bag_quantity(fare_detail.includedCheckedBags);
      parsed.included += traveler.included;

      traveler.cabin = bag_quantity(fare_detail.includedCabinBags);
      parsed.cabin += traveler.cabin;

      traveler.hand = bag_quantity(fare_detail.includedHandBags);
      parsed.hand += traveler.hand;

      const { additionalServices = [] } = fare_detail;
      const list = Array.isArray(additionalServices) ? additionalServices : [];
      traveler.chargeable = list
        .filter((entry) => entry && entry.type === 'bag')
        .reduce((sum, entry) => sum + bag_quantity(entry), 0);
      parsed.chargeable += traveler.chargeable;
    }

    parsed.travelers.push(traveler);
  });

  parsed.total = parsed.included + parsed.chargeable;
  return parsed;
}

/**
 * True when the offer carries at least one chargeable bag for this traveler.
 * Without this override the base default (`false`) hides the CHECKED_BAGS
 * section on the confirm and checkout pages for Kyte offers.
 * @param {object} flight_offer
 * @param {string} traveler_id
 * @returns {boolean}
 */
export function has_chargeable_bags(flight_offer, traveler_id) {
  const { travelerPricings = [] } = flight_offer || {};
  const pricing = travelerPricings.find(
    (t) => String((t || {}).travelerId) === String(traveler_id)
  );
  const { fareDetailsBySegment = [] } = pricing || {};
  return fareDetailsBySegment.some((fdbs) => {
    const { additionalServices = [] } = fdbs || {};
    const list = Array.isArray(additionalServices) ? additionalServices : [];
    return list.some((entry) => {
      const { type = '', quantity = 0 } = entry || {};
      return type === 'bag' && Number(quantity) > 0;
    });
  });
}

const build_catalog_row = (source, segmentIds, shared) => {
  const {
    decimals,
    currencyCode,
    currencyGlyph,
    provider_id,
    travelerIds,
    type,
  } = shared;
  const { purchaseLimitations = {} } = source;
  const total = minor_to_decimal(source.price, decimals);
  const info = get_kyte_ancillary_info(source, type);
  return {
    id: source.id,
    name: info.txt,
    subtitle: info.subtitle,
    icon: info.icon,
    // Catalog rows are 1 unit each; UI increments by 1 up to
    // purchaseQuantity (the max the upstream purchaseLimitations
    // allows for this ancillary).
    quantity: 1,
    purchaseQuantity: as_number(purchaseLimitations.purchaseQuantity, 1) || 1,
    incompatibleWith: as_array(purchaseLimitations.incompatibleWith),
    requiredOnAllSegments: as_boolean(
      purchaseLimitations.requiredOnAllSegments
    ),
    combineWith: as_array(purchaseLimitations.combineWith),
    travelerIds,
    segmentIds,
    purchaseSegmentIds: as_array(source.segments),
    bookableByItinerary: false,
    price: {
      total,
      grandTotal: total,
      base: total,
      taxes: [],
      currency: currencyCode || '',
      currencySymbol: currencyGlyph,
    },
    _provider: provider_id,
    _raw: source,
  };
};

export const build_extra_bags_catalog = (
  raw_catalog,
  flight_offer,
  provider,
  currency,
  type
) => {
  const safe = raw_catalog || {};
  const ids = Object.keys(safe);
  if (!ids.length) return null;

  const travelerIds = collect_traveler_ids(flight_offer);
  const safe_currency = currency || { code: 'EUR', decimals: 2 };
  const { code: currencyCode = 'EUR', decimals = 2 } = safe_currency;
  const currencyGlyph = currency_symbol(currencyCode);
  const { providerId: provider_id = 'kyte' } = provider || {};
  const shared = {
    decimals,
    currencyCode,
    currencyGlyph,
    provider_id,
    travelerIds,
    type,
  };
  const catalog = {};

  ids.forEach((bagId) => {
    const source = safe[bagId];
    if (!source || NOT_SOLD_ANCILLARY_CODES.has(source.id)) return;
    const { segments = [] } = source;
    // Journey-level ancillaries (excess weight, Flex Pass) arrive as ONE row
    // spanning every segment, unlike the per-leg bags Kyte splits into a base
    // row + a `_RT` twin. The Segment→Traveler grouper keys each row to a
    // single segment (segmentIds[0]), so a multi-segment row would render on
    // the first leg only — fan those out, one row per leg.
    // Keys always end in `:<segment>` (flatten_selections splits on it);
    // Wizzair ids already do.
    let leg_ids = segments;
    if (!leg_ids.length) leg_ids = [''];
    leg_ids.forEach((segmentId) => {
      let key = bagId;
      if (segmentId && !bagId.endsWith(`:${segmentId}`)) {
        key = `${bagId}:${segmentId}`;
      }
      let segmentIds = [];
      if (segmentId) segmentIds = [segmentId];
      catalog[key] = build_catalog_row(source, segmentIds, shared);
    });
  });

  return catalog;
};
